/**
 * WeatherService -- Fetches and caches weather data from Open-Meteo (free, no API key).
 */

import type { WeatherData } from '../modules/home/types';
import type { LocationService } from './LocationService';

const LS_KEY = 'aweni_weather_cache';
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

export class WeatherService {
  private data: WeatherData | null = null;
  private locationService: LocationService;
  private fetching = false;

  constructor(locationService: LocationService) {
    this.locationService = locationService;
    this.loadFromCache();
  }

  getData(): WeatherData | null {
    return this.data;
  }

  private loadFromCache(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as WeatherData;
        if (Date.now() - parsed.fetchedAt < CACHE_DURATION_MS) {
          this.data = parsed;
        }
      }
    } catch { /* ignore */ }
  }

  private saveToCache(): void {
    try {
      if (this.data) {
        localStorage.setItem(LS_KEY, JSON.stringify(this.data));
      }
    } catch { /* ignore */ }
  }

  async fetch(): Promise<WeatherData | null> {
    if (this.fetching) return this.data;
    if (this.data && Date.now() - this.data.fetchedAt < CACHE_DURATION_MS) {
      return this.data;
    }

    const loc = this.locationService.getState();
    if (loc.lat === null || loc.lng === null) return null;

    this.fetching = true;
    try {
      const url =
        `https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lng}` +
        `&current=temperature_2m,apparent_temperature,rain,relative_humidity_2m,wind_speed_10m` +
        `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum` +
        `&hourly=rain` +
        `&forecast_days=1` +
        `&timezone=auto`;

      const resp = await fetch(url, { cache: 'no-store' });
      if (!resp.ok) return this.data;

      const json = await resp.json();
      const current = json.current;
      const daily = json.daily;

      // Extract hourly rain for today (to identify rain windows)
      const hourlyRain: { hour: number; rain: number }[] = [];
      if (json.hourly?.rain && json.hourly?.time) {
        const times = json.hourly.time as string[];
        const rains = json.hourly.rain as number[];
        for (let i = 0; i < Math.min(24, times.length); i++) {
          hourlyRain.push({ hour: i, rain: rains[i] || 0 });
        }
      }

      this.data = {
        temperature: current?.temperature_2m ?? 0,
        feelsLike: current?.apparent_temperature ?? current?.temperature_2m ?? 0,
        tempMax: daily?.temperature_2m_max?.[0] ?? 0,
        tempMin: daily?.temperature_2m_min?.[0] ?? 0,
        rain: current?.rain ?? 0,
        humidity: current?.relative_humidity_2m ?? 0,
        windSpeed: current?.wind_speed_10m ?? 0,
        precipitationProbability: daily?.precipitation_probability_max?.[0] ?? 0,
        hourlyRain,
        fetchedAt: Date.now(),
        city: loc.city || undefined,
      };

      this.saveToCache();
      return this.data;
    } catch {
      return this.data;
    } finally {
      this.fetching = false;
    }
  }
}
